import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.trackyourwheelz.com/api';

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const invoiceRef = useRef(null);
  const { get, post } = useApi();
  const { user, token } = useAuth();

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    setLoading(true);
    setError(null);

    try {
      const invoiceId = id;
      
      
      if (!invoiceId || invoiceId === 'undefined') {
        throw new Error('Invoice ID not found or undefined');
      }

      
      // Fetch invoice data directly
      const invoice = await get(`/invoices/${invoiceId}`);
      
      if (!invoice) {
        throw new Error('Invoice not found');
      }


      // Set invoice data
      setInvoiceData(invoice);
      
      // Set quote data if available
      if (invoice.quote) {
        setQuoteData(invoice.quote);
      }
      
      // Fetch account data
      try {
        const accountData = await get(`/accounts/${invoice.account_id}`);
        setAccountData(accountData);
      } catch (accountErr) {
      }
    } catch (err) {
      setError(err.message || 'An error occurred while loading the invoice');
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const displayData = {
    invoice_number: invoiceData?.invoice_number || 'N/A',
    invoice_date: invoiceData?.issue_date || 'N/A',
    due_date: invoiceData?.due_date || 'N/A',
    subtotal: parseFloat(invoiceData?.subtotal || 0),
    cgst: parseFloat(invoiceData?.cgst_amount || 0),
    sgst: parseFloat(invoiceData?.sgst_amount || 0),
    total_tax: parseFloat(invoiceData?.total_tax || 0),
    total: parseFloat(invoiceData?.total || 0),
    gst_type: invoiceData?.gst_type || 'intra',
    cgst_rate: parseFloat(invoiceData?.cgst_rate || 9),
    sgst_rate: parseFloat(invoiceData?.sgst_rate || 9),
    status: invoiceData?.status || 'draft',
    notes: invoiceData?.notes || '',
    items: invoiceData?.items || [],
  };

  const accountInfo = accountData || {};
  const quoteInfo = quoteData || {};

  const normalizedRole = (user?.role || '').toLowerCase();
  const isSuperAdmin = ['superadmin', 'super_admin', 'super-admin'].includes(normalizedRole);
  const invoiceIdForUpload =
    invoiceData?.id || invoiceData?._id || invoiceData?.invoice_id || id;

  const printInvoice = () => {
    if (!invoiceData) return;

    const originalTitle = document.title;
    const invoiceNumber = displayData.invoice_number || 'INV-UNKNOWN';
    const currentDate = new Date().toISOString().split('T')[0];
    const defaultFilename = `${invoiceNumber}_${currentDate}`;

    document.title = defaultFilename;

    window.print();

    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const downloadInvoice = () => {
    printInvoice();
    toast.success('Invoice download initiated');
  };

  const generateInvoicePdfBlob = useCallback(async () => {
    if (!invoiceRef.current) {
      throw new Error('Invoice layout is not ready yet.');
    }

    const printableNode =
      invoiceRef.current.querySelector('.invoice-page') || invoiceRef.current;

    const targetScale = Math.min(window.devicePixelRatio || 1, 1.5);

    const canvas = await html2canvas(printableNode, {
      scale: targetScale,
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.8);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;
    }

    return pdf.output('blob');
  }, []);

  const handleUploadPdf = useCallback(async () => {
    if (!invoiceIdForUpload) {
      toast.error('Unable to send PDF. Missing invoice ID.');
      return;
    }

    if (!token) {
      toast.error('Authentication token missing. Please re-login and try again.');
      return;
    }

    try {
      setUploadingPdf(true);
      const pdfBlob = await generateInvoicePdfBlob();
      const fileName = `${displayData.invoice_number || invoiceIdForUpload}.pdf`;
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);

      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceIdForUpload}/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to send PDF.');
      }

      toast.success('Invoice PDF sent successfully.');
    } catch (error) {
      toast.error(error.message || 'Failed to send invoice PDF.');
    } finally {
      setUploadingPdf(false);
    }
  }, [
    displayData.invoice_number,
    generateInvoicePdfBlob,
    invoiceIdForUpload,
    token,
  ]);

  if (loading) {
    return (
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
        <div className="max-w-4xl mx-auto">
          {/* Header Skeleton */}
          <div className="bg-gradient-to-r p-4 mb-4">
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <Skeleton className="h-8 w-48 bg-white/20" />
                <Skeleton className="h-4 w-32 bg-white/20" />
                <div className="space-y-1">
                  <Skeleton className="h-3 w-40 bg-white/20" />
                  <Skeleton className="h-3 w-36 bg-white/20" />
                  <Skeleton className="h-3 w-38 bg-white/20" />
                  <Skeleton className="h-3 w-42 bg-white/20" />
                </div>
              </div>
              <Skeleton className="h-12 w-24 bg-white/20" />
            </div>
          </div>

          <div className="border-muted-foreground/10 border-1 shadow-lg bg-card">
            <div className="p-3 bg-muted/50 border-b border-border">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Skeleton className="h-4 w-16 mb-2" />
                  <div className="bg-card p-2 rounded-lg border-muted-foreground/10 border-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <div className="flex justify-between">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <div className="bg-card p-2 rounded-lg border-muted-foreground/10 border-1 space-y-2">
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <div className="flex justify-between">
                        <Skeleton className="h-3 w-18" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
        <div className="max-w-4xl mx-auto text-center py-8">
          <div className="text-red-500 mb-4">
            <p className="text-lg font-semibold">Error loading invoice</p>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={loadInvoice} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
        <div className="max-w-4xl mx-auto text-center py-8">
          <p className="text-lg font-semibold">Invoice not found</p>
          <Button onClick={() => navigate('/admin/quotes')} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="invoice-print-surface p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen"
      ref={invoiceRef}
    >
      {/* Print Styles */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html,
          body {
            background: white !important;
            color: black !important;
          }

          .invoice-print-surface {
            background: white !important;
            color: black !important;
          }

          /* Hide header and navigation elements */
          header,
          nav,
          .header,
          .navbar,
          .navigation,
          .sidebar,
          .admin-header,
          .main-header,
          .top-nav,
          .app-header {
            display: none !important;
          }
          
          /* Hide any fixed/sticky elements */
          .fixed,
          .sticky,
          .top-0,
          .z-50,
          .z-40,
          .z-30 {
            position: static !important;
          }
          
        }
      `}</style>
      {/* Print Styles */}
      <style>{`
        @media print {
          .invoice-wrapper {
            width: 190mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
          }

          .invoice-page {
            width: 100% !important;
            box-shadow: none !important;
            border: 1px solid #000 !important;
            background: white !important;
            color: black !important;
            page-break-inside: avoid !important;
          }

          .invoice-page * {
            color: black !important;
            background: transparent !important;
          }
          
          .invoice-page h1,
          .invoice-page h2,
          .invoice-page h3,
          .invoice-page p,
          .invoice-page span,
          .invoice-page div,
          .invoice-page td,
          .invoice-page th,
          .invoice-page .text-foreground,
          .invoice-page .text-muted-foreground,
          .invoice-page .text-primary-foreground {
            color: black !important;
            background: transparent !important;
          }
          
          .invoice-page .bg-gradient-to-r {
            background: #f5f5f5 !important;
            color: black !important;
          }
          
          .invoice-page .bg-muted\/50 {
            background: #f9f9f9 !important;
          }
          
          .invoice-page .border-muted-foreground\/10 {
            border-color: #000 !important;
          }
          
          .invoice-page table {
            border-collapse: collapse !important;
            border: 2px solid #000 !important;
          }
          
          .invoice-page th,
          .invoice-page td {
            border: 1px solid #000 !important;
            color: black !important;
            background: transparent !important;
            padding: 8px !important;
          }
          
          .invoice-page th {
            background: #f0f0f0 !important;
            font-weight: bold !important;
            color: black !important;
          }
          
          .invoice-page .bg-card {
            background: transparent !important;
          }
          
          .invoice-page .font-medium,
          .invoice-page .font-bold,
          .invoice-page .font-semibold {
            color: black !important;
            font-weight: bold !important;
          }
          
          .invoice-page .border-t {
            border-top: 2px solid #000 !important;
          }
          
          .invoice-page .border-b {
            border-bottom: 1px solid #000 !important;
          }
          
          /* Hide action buttons when printing */
          .no-print {
            display: none !important;
          }
          
          /* Hide URL from print */
          @page {
            margin: 0.5in;
            @bottom-right {
              content: none !important;
            }
            @bottom-left {
              content: none !important;
            }
            @bottom-center {
              content: none !important;
            }
            @top-right {
              content: none !important;
            }
            @top-left {
              content: none !important;
            }
            @top-center {
              content: none !important;
            }
          }
          
          /* Hide browser URL and navigation elements */
          body::before,
          body::after {
            display: none !important;
          }
          
        }
      `}</style>
      <div className="invoice-wrapper">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto border-muted-foreground/10 border-1 shadow-lg bg-card invoice-page">
        <div className="bg-gradient-to-r  from-primary to-primary/80 text-primary-foreground p-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-1">TrackYourWheelz</h1>
              <p className="text-primary-foreground/80 text-xs leading-tight mb-1">
                Vehicle Tracking System
              </p>
              <div className="text-xs text-primary-foreground/80 space-y-0.5">
                <p className="leading-tight">Building / Flat No : PLOT NO 75,</p>
                <p className="leading-tight">Road / Street : VISHWAKARMA COLONY</p>
                <p className="leading-tight">Locality/Sub Locality : Suraram</p>
                <p className="leading-tight">City / Town / Village : Hyderabad</p>
                <p className="leading-tight">District : Medchal Malkajgiri</p>
                <p className="leading-tight">State : Telangana</p>
                <p className="leading-tight">GSTIN : 36AAWFT3819N1Z8</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-card text-foreground px-3 py-1 rounded-lg">
                <h2 className="text-xl font-bold">TAX INVOICE</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Details Section */}
        <div className="p-3 bg-muted/50 border-b border-border">
          <div className="grid grid-cols-2 gap-4">
            {/* Bill To Section */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Bill To:</h3>
              <div className="bg-card p-2 rounded-lg border-muted-foreground/10 border-1 ">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Customer Name:</span> 
                  <p className="font-medium text-foreground flex justify-end text-sm">{accountInfo?.name || 'N/A'}</p>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Company:</span> 
                  <span className="font-medium text-xs">{accountInfo?.company_name || 'N/A'}</span>
                </div>
                
                <div className="flex justify-between">
                  <div>
                    <span className="text-muted-foreground text-xs">Address:</span>
                  </div>
                  <div>
                    <span className="font-medium text-xs">
                      {accountInfo?.address ? 
                        (typeof accountInfo.address === 'string' ? 
                          accountInfo.address : 
                          `${accountInfo.address.house_no || ''} ${accountInfo.address.street || ''} ${accountInfo.address.area || ''}`.trim() || 'N/A'
                        ) : 'N/A'
                      }
                    </span>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">City, State:</span>
                  <p className="font-medium text-xs">
                    {accountInfo?.city || 'N/A'}, {accountInfo?.state || 'N/A'}
                  </p>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Pincode:</span> 
                  <span className="font-medium text-xs">{accountInfo?.pincode || 'N/A'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Phone:</span> 
                  <span className="font-medium text-xs">{accountInfo?.phone || 'N/A'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">GST Number:</span> 
                  <span className="font-medium text-xs">{accountInfo?.gst_number || 'N/A'}</span>
                </div>
              </div>
            </div>
            
            {/* Invoice Info Section */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Invoice Details:</h3>
              <div className="bg-card p-2 rounded-lg border-muted-foreground/10 border-1  space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Invoice Number:</span>
                  <span className="font-medium text-xs">{displayData.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Invoice Date:</span>
                  <span className="font-medium text-xs">{formatDate(displayData.invoice_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Due Date:</span> 
                  <span className="font-medium text-xs">{formatDate(displayData.due_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Quote Number:</span>
                  <span className="font-medium text-xs">{quoteInfo?.quote_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Payment Terms:</span>
                  <span className="font-medium text-xs">{displayData.notes || 'Net 15 Days'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-xs">Status:</span>
                  <span className="font-medium text-xs capitalize">{displayData.status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table Section */}
        <div className="p-3">
          <table className="w-full border-muted-foreground/10 border-1 ">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">Description</th>
                <th className="text-center p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">HSN Code</th>   
                <th className="text-center p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">Quantity</th>
                <th className="text-center p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">Unit</th>
                <th className="text-right p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">Rate (₹)</th>
                <th className="text-right p-2 border-muted-foreground/10 border-1 font-semibold text-foreground text-xs">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {displayData.items.map((item, index) => (
                <tr key={index} className="hover:bg-muted/30">
                  <td className="p-2 border-muted-foreground/10 border-1 text-foreground text-xs">{item.description}</td>
                  <td className="p-2 border-muted-foreground/10 border-1 text-center text-foreground text-xs">25051011</td>
                  <td className="p-2 border-muted-foreground/10 border-1 text-center text-foreground text-xs">{item.quantity}</td>
                  <td className="p-2 border-muted-foreground/10 border-1 text-center text-foreground text-xs">Units</td>
                  <td className="p-2 border-muted-foreground/10 border-1 text-right text-foreground text-xs">{formatCurrency(parseFloat(item.unit_price))}</td>
                  <td className="p-2 border-muted-foreground/10 border-1 text-right font-medium text-foreground text-xs">{formatCurrency(parseFloat(item.total_price))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals Section */}
          <div className="flex justify-end mt-3">
            <div className="w-64 space-y-1">
              <div className="flex justify-between text-muted-foreground text-xs">
                <span>Taxable Amount:</span>
                <span>{formatCurrency(displayData.subtotal)}</span>
              </div>
              
              {/* CGST & SGST (Intrastate) */}
              <div className="space-y-1">
                <div className="flex justify-between text-muted-foreground text-xs">
                  <span>CGST ({displayData.cgst_rate}%):</span>
                  <span>{formatCurrency(displayData.cgst)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground text-xs">
                  <span>SGST ({displayData.sgst_rate}%):</span>
                  <span>{formatCurrency(displayData.sgst)}</span>
                </div>
              </div>

              <div className="border-t border-border pt-1">
                <div className="flex justify-between text-sm font-bold text-foreground">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(displayData.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Terms and Conditions & Bank Details Section */}
        <div className="p-3 bg-muted/50 border-t border-border">
          <div className="flex gap-6">
            
            
            {/* Terms and Conditions Section (Right Side) */}
            <div className="w-32 flex-auto">
              <h3 className="text-sm font-semibold text-foreground mb-2">Terms & Conditions:</h3>
              <div className="text-xs text-muted-foreground space-y-0">
                <p>• Payment is due depends on the terms and conditions of the sales order</p>
                <p>• Late payments may incur additional charges</p>
                <p>• Goods once delivered will not be taken back</p>
                <p>• All disputes are subject to Government</p>
                <p>• This is a computer generated invoice</p>
              </div>
            </div>
             {/* Terms and Conditions Section (Right Side) */}
             <div className="w-32 flex-auto">
              <h3 className="text-sm font-semibold text-foreground mb-2">Bank Details:</h3>
              <div className="text-xs text-muted-foreground space-y-0">
                <p>Bank Name: THE ADARSH CO-OP. URBAN BANK LTD.</p>
                <p>Account Number: 100110141137178</p>
                <p>Account Name: TrackYourWheelz</p>
                <p>IFSC Code: ADBK0001001</p>
                <p>Branch: JEEDIMETLA</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="sticky bottom-0 bg-muted text-muted-foreground p-2 text-center text-xs border-t border-border">
          <div className="flex justify-between items-center">
            <div className="text-muted-foreground">
              <p>Thank you for your business!</p>
            </div>
            <div className="text-muted-foreground">
              <p>Generated on: {new Date().toLocaleString('en-IN')}</p>
            </div>
          </div>
          <div className="mt-1 text-muted-foreground/70">
            <p>TrackYourWheelz | GSTIN: 36AAWFT3819N1Z8 | PAN: AAWFT3819N</p>
          </div>
        </div>
      </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-6 no-print">
        <Button onClick={downloadInvoice} variant="outline" className="px-6 py-3 rounded-lg transition-colors font-medium shadow-lg text-sm border-muted-foreground/10 border-1">
          <Download className="h-4 w-4 mr-2" />
          Download Invoice
        </Button>
        <Button onClick={printInvoice} variant="outline" className="px-6 py-3 rounded-lg transition-colors font-medium shadow-lg text-sm border-muted-foreground/10 border-1">
          <Printer className="h-4 w-4 mr-2" />
          Print Invoice
        </Button>
        {isSuperAdmin && (
          <Button
            onClick={handleUploadPdf}
            variant="outline"
            disabled={uploadingPdf}
            className="px-6 py-3 rounded-lg transition-colors font-medium shadow-lg text-sm border-muted-foreground/10 border-1"
          >
            {uploadingPdf ? 'Sending...' : 'Send PDF'}
          </Button>
        )}
        <Button onClick={() => navigate('/admin/quotes')} variant="outline" className="px-6 py-3 rounded-lg transition-colors font-medium shadow-lg text-sm border-muted-foreground/10 border-1">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Quotes
        </Button>
      </div>

    </div>
  );
}